import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, Loader2, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { emailApi, type MailboxFilterRule } from "@/lib/api";

interface Props {
  /** The mailbox this dialog was opened from (used for new mailbox-specific rules and to scope the list). */
  mailbox: string | null;
  open: boolean;
  onClose: () => void;
  /** My Mail (personal mailbox) case: hide "Apply to all mailboxes" — a personal rule must
   * always be scoped to the owner's own mailbox, never promoted to a firm-wide rule. */
  hideApplyToAll?: boolean;
}

/** W3 noise filters — light admin panel for firm-wide + per-mailbox filter rules. */
export function MailboxFilterRulesDialog({ mailbox, open, onClose, hideApplyToAll }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [pattern, setPattern] = useState("");
  const [matchField, setMatchField] = useState<"subject" | "sender">("subject");
  const [applyToAll, setApplyToAll] = useState(!mailbox && !hideApplyToAll);
  const [saving, setSaving] = useState(false);

  const { data: rules = [], isLoading } = useQuery<MailboxFilterRule[]>({
    queryKey: ["mailbox-filter-rules", mailbox],
    queryFn: () => emailApi.getFilterRules(mailbox ?? undefined),
    enabled: open,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["mailbox-filter-rules"] });
    queryClient.invalidateQueries({ queryKey: ["inbox"] });
    queryClient.invalidateQueries({ queryKey: ["inbox-mine"] }); // My Mail's Filtered bucket
  };

  const addRule = async () => {
    const p = pattern.trim();
    if (!p) return;
    setSaving(true);
    try {
      await emailApi.addFilterRule({
        mailbox_address: hideApplyToAll ? mailbox : (applyToAll ? null : mailbox),
        match_field: matchField,
        pattern: p,
      });
      setPattern("");
      invalidateAll();
    } catch (err: any) {
      toast({ title: "Could not add rule", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule: MailboxFilterRule) => {
    try {
      await emailApi.toggleFilterRule(rule.id, !rule.is_active);
      invalidateAll();
    } catch (err: any) {
      toast({ title: "Could not update rule", description: err.message, variant: "destructive" });
    }
  };

  const deleteRule = async (rule: MailboxFilterRule) => {
    try {
      await emailApi.deleteFilterRule(rule.id);
      invalidateAll();
    } catch (err: any) {
      toast({ title: "Could not delete rule", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filter rules{mailbox ? ` — ${mailbox}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
            </div>
          ) : rules.length === 0 ? (
            <div className="rounded border border-dashed py-6 text-center text-sm text-muted-foreground">
              No filter rules yet.
            </div>
          ) : (
            <div className="max-h-64 space-y-1.5 overflow-auto">
              {rules.map((rule) => (
                <div key={rule.id} className="flex items-center gap-2 rounded border px-2 py-1.5 text-sm">
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {rule.match_field}
                  </Badge>
                  {rule.mailbox_address === null && (
                    <Badge className="shrink-0 bg-blue-600 text-[10px] hover:bg-blue-600">All mailboxes</Badge>
                  )}
                  <span className="min-w-0 flex-1 truncate" title={rule.pattern}>
                    {rule.pattern}
                  </span>
                  <Switch checked={rule.is_active} onCheckedChange={() => toggleRule(rule)} />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    title="Delete rule"
                    onClick={() => deleteRule(rule)}
                  >
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2 rounded border p-2.5">
            <div className="flex gap-2">
              <Select value={matchField} onValueChange={(v) => setMatchField(v as "subject" | "sender")}>
                <SelectTrigger className="w-28 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject">Subject</SelectItem>
                  <SelectItem value="sender">Sender</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addRule()}
                placeholder="Text to match…"
              />
            </div>
            <div className="flex items-center justify-between">
              {hideApplyToAll ? (
                <span />
              ) : (
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox checked={applyToAll} onCheckedChange={(v) => setApplyToAll(!!v)} />
                  Apply to all mailboxes
                </label>
              )}
              <Button size="sm" onClick={addRule} disabled={saving || !pattern.trim()}>
                Add rule
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
